import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowRight, Shield, Truck, Sparkles, HeartHandshake } from "lucide-react";
import { fetchProducts, formatUsd } from "../lib/api";
import { HOME } from "../constants/testIds";

const HOW_IT_WORKS = [
  { n: "01", title: "Choose Your Procedure", desc: "Select from six curated recovery systems tailored to your surgery." },
  { n: "02", title: "Personalize", desc: "Tell us your surgery date and we time delivery for pre-op." },
  { n: "03", title: "We Deliver", desc: "Everything arrives before surgery in premium packaging." },
  { n: "04", title: "Recover With Confidence", desc: "Week-by-week protocol guides you from Day 0 to Week 6+." },
];

const TESTIMONIALS = [
  { name: "Dr. L. Morgan, MD", role: "Board-Certified Plastic Surgeon", quote: "The most thoughtful post-op kit I've seen in 15 years of practice.",
    avatar: "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?auto=format&fit=crop&w=200&q=80" },
  { name: "Sofia R.", role: "Tummy Tuck, Age 34", quote: "It felt like a gift the day it arrived. Every item had a purpose.",
    avatar: "https://images.unsplash.com/photo-1531123897727-8f129e1688ce?auto=format&fit=crop&w=200&q=80" },
  { name: "Amara J.", role: "Facelift, Age 52", quote: "The cold mask alone was worth it. I felt genuinely cared for.",
    avatar: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&w=200&q=80" },
];

const PRESS = ["Vogue", "Harper's Bazaar", "Byrdie", "Allure", "Well+Good", "Refinery29", "Marie Claire", "Goop"];

export default function HomePage() {
  const [products, setProducts] = useState([]);
  useEffect(() => { fetchProducts().then(setProducts).catch(() => {}); }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-20 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 fade-up">
            <div className="eyebrow">Surgeon-Approved Aftercare</div>
            <h1 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-7xl leading-[1.05] tracking-tight">
              Your Procedure. <br/>
              <span className="italic text-[#B85C56]">Our Recovery System.</span>
            </h1>
            <p className="mt-8 max-w-lg text-base sm:text-lg text-[#6B5757] leading-relaxed">
              Curated, surgeon-backed post-cosmetic recovery systems delivered directly to your door before your procedure date.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/shop" data-testid={HOME.shopNowBtn} className="btn-primary inline-flex items-center gap-3">
                Shop by Procedure <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/finder" data-testid={HOME.findYourSystemBtn} className="btn-outline inline-flex items-center gap-3">
                Find by Surgery Date
              </Link>
            </div>
            <div className="mt-16 flex flex-wrap gap-8 text-xs text-[#6B5757] tracking-wider">
              <span>12,000+ patients recovered</span>
              <span className="text-[#E5CDBB]">|</span>
              <span>240+ Board-Certified Surgeons</span>
            </div>
          </div>
          <div className="lg:col-span-5 relative fade-up">
            <div className="relative aspect-[4/5] overflow-hidden">
              <img
                src="/generated/hero_liposuction.png"
                alt="ALTA Recovery"
                className="w-full h-full object-cover"
              />
              <div className="absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-t from-[#2A1F1F]/60 to-transparent" />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-[#FBF6F1] border border-[#F0DFD0] p-6 shadow-[0_12px_36px_-6px_rgba(20,19,18,0.12)] hidden md:block">
              <div className="eyebrow">Delivered in</div>
              <div className="font-serif text-3xl mt-1">3–5 days</div>
              <div className="text-xs text-[#6B5757] mt-1">before your surgery</div>
            </div>
          </div>
        </div>
      </section>

      <div className="hairline max-w-7xl mx-auto" />

      {/* PRESS BAR */}
      <section className="py-10 border-b border-[#F0DFD0] overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <div className="eyebrow text-center">As Featured In</div>
        </div>
        <div className="mt-6 relative overflow-hidden">
          <div className="marquee flex whitespace-nowrap items-center gap-16 text-2xl sm:text-3xl text-[#6B5757]" style={{ width: "max-content" }}>
            {[...PRESS, ...PRESS].map((p, i) => (
              <span key={i} className="font-serif italic opacity-70 hover:opacity-100 transition-opacity">{p}</span>
            ))}
          </div>
        </div>
      </section>

      {/* BRAND MARQUEE */}
      <section className="py-12 border-b border-[#F0DFD0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <div className="eyebrow text-center">Curated From The Brands Surgeons Already Trust</div>
          <div className="mt-8 flex flex-wrap justify-center items-center gap-x-12 gap-y-6 font-serif text-lg sm:text-xl text-[#6B5757]">
            <span>Marena</span>
            <span className="text-[#E5CDBB]">·</span>
            <span>SkinCeuticals</span>
            <span className="text-[#E5CDBB]">·</span>
            <span>Alastin</span>
            <span className="text-[#E5CDBB]">·</span>
            <span>Silagen</span>
            <span className="text-[#E5CDBB]">·</span>
            <span className="italic">Augustinus Bader</span>
            <span className="text-[#E5CDBB]">·</span>
            <span>Charlotte Tilbury</span>
            <span className="text-[#E5CDBB]">·</span>
            <span>iS Clinical</span>
            <span className="text-[#E5CDBB]">·</span>
            <span>LARQ</span>
            <span className="text-[#E5CDBB]">·</span>
            <span>Amoena</span>
            <span className="text-[#E5CDBB]">·</span>
            <span>Kelo-Cote</span>
          </div>
        </div>
      </section>

      {/* SHOP BY PROCEDURE */}
      <section className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
            <div>
              <div className="eyebrow">The Systems</div>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-4">Shop by Procedure</h2>
            </div>
            <Link to="/shop" className="nav-link text-sm text-[#4A3535]">
              View All Systems →
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.map((p) => (
              <Link
                key={p.id}
                to={`/products/${p.id}`}
                data-testid={HOME.procedureCard(p.id)}
                className="group block"
              >
                <div className="relative aspect-[3/4] overflow-hidden bg-[#FDF3ED]">
                  <img src={p.image} alt={p.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute top-4 right-4 bg-[#FBF6F1] px-3 py-1 font-mono text-[10px] tracking-widest">
                    {p.duration_weeks}-WK
                  </div>
                </div>
                <div className="mt-5 flex items-start justify-between gap-4">
                  <div>
                    <div className="eyebrow">{p.procedure}</div>
                    <h3 className="font-serif text-xl mt-2 group-hover:text-[#B85C56] transition-colors">{p.name}</h3>
                    <p className="text-xs text-[#6B5757] mt-2 leading-relaxed">{p.subtitle}</p>
                  </div>
                  <div className="text-right">
                    <div className="font-serif text-lg">{formatUsd(p.price)}</div>
                    <div className="font-mono text-[10px] text-[#A08D8D]">★ {p.rating}</div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* MORE THAN A BOX */}
      <section className="bg-[#2A1F1F] text-[#FDF3ED] py-24 lg:py-32 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="eyebrow">Philosophy</div>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-6xl mt-6 leading-[1.1]">
              More Than a Box. <br/><span className="italic text-[#B89B66]">A Better Recovery.</span>
            </h2>
            <p className="mt-8 text-[#E5C1AF] max-w-md leading-relaxed">
              Every ALTA system was built with surgeons and patients. The right item, at the right moment, in the right ritual — so healing feels like a spa, not a trial.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-6">
            {[
              { icon: Shield, title: "Medical Grade", desc: "Surgeon-vetted materials only." },
              { icon: Truck, title: "Pre-Op Delivery", desc: "Arrives before your surgery date." },
              { icon: Sparkles, title: "Spa Considered", desc: "Elegant packaging, guided rituals." },
              { icon: HeartHandshake, title: "Cared For", desc: "Live support with recovery guides." },
            ].map((f) => (
              <div key={f.title} className="border border-[#3A2A2A] p-6">
                <f.icon className="w-6 h-6 text-[#B89B66]" strokeWidth={1.2} />
                <h4 className="font-serif text-lg mt-4">{f.title}</h4>
                <p className="text-xs text-[#A08D8D] mt-2 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <div className="text-center mb-16">
            <div className="eyebrow">How It Works</div>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-4">Four Steps to Better Recovery</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
            {HOW_IT_WORKS.map((s) => (
              <div key={s.n} className="border-t border-[#2A1F1F] pt-6">
                <div className="font-mono text-xs tracking-widest text-[#B85C56]">{s.n}</div>
                <h3 className="font-serif text-2xl mt-4">{s.title}</h3>
                <p className="text-sm text-[#6B5757] mt-3 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-[#FDF3ED] py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <div className="eyebrow text-center">Clinical Credibility</div>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-center mt-4">Trusted by Surgeons & Patients</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16">
            {TESTIMONIALS.map((t) => (
              <blockquote key={t.name} className="bg-white p-8 border border-[#F0DFD0] rounded-xl">
                <div className="flex items-center gap-3 mb-6">
                  <img src={t.avatar} alt={t.name} className="w-12 h-12 rounded-full object-cover" />
                  <div>
                    <div className="font-medium text-sm text-[#2A1F1F]">{t.name}</div>
                    <div className="text-xs text-[#6B5757] mt-0.5">{t.role}</div>
                  </div>
                </div>
                <div className="font-serif text-4xl text-[#B85C56] leading-none">"</div>
                <p className="font-serif text-lg text-[#2A1F1F] leading-snug mt-2 italic">{t.quote}</p>
              </blockquote>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
